/**
 * AudioRecorderService.js
 * Handles audio recording, chunking, and format conversion for real-time transcription
 */

import { rmsLevelFromTimeDomain } from "@/utils/audioRms";

export class AudioRecorderService {
  constructor() {
    this.mediaRecorder = null;
    this.audioContext = null;
    this.audioAnalyser = null;
    this.microphone = null;
    this.processor = null;
    this.isRecording = false;
    this.isPaused = false;
    this.recordedChunks = [];
    this.chunkBuffer = [];
    this.chunkDuration = 500; // 0.5 second chunks for faster upload
    this._stopResolve = null;
    this._stopTimeout = null;
    this.listeners = {
      onChunk: null,
      onAudioLevel: null,
      onError: null,
      onStop: null,
      onAudioData: null,
    };
  }

  /**
   * Initialize audio recording with proper format for AssemblyAI
   * @returns {Promise<void>}
   */
  async initialize() {
    console.log("[AudioRecorder] Initializing audio recorder...");
    try {
      // Request microphone permission
      // Note: Most browsers will use their default sample rate (usually 48kHz)
      // We'll downsample in the audio processing stage
      console.log("[AudioRecorder] Requesting microphone permission...");
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          channelCount: 1,
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
      });
      console.log(
        "[AudioRecorder] Microphone permission granted, stream:",
        stream,
      );

      // Setup MediaRecorder with WebM/Opus format
      const options = {
        mimeType: "audio/webm;codecs=opus",
      };

      // Check if the browser supports the MIME type
      if (!MediaRecorder.isTypeSupported(options.mimeType)) {
        console.warn(
          "[AudioRecorder] WebM/Opus not supported, falling back to default",
        );
        this.mediaRecorder = new MediaRecorder(stream);
      } else {
        console.log("[AudioRecorder] Creating MediaRecorder with WebM/Opus");
        this.mediaRecorder = new MediaRecorder(stream, options);
      }

      // Setup audio context for level monitoring and processing
      this.audioContext = new (globalThis.AudioContext ||
        globalThis.webkitAudioContext)();
      this.microphone = this.audioContext.createMediaStreamSource(stream);
      this.audioAnalyser = this.audioContext.createAnalyser();
      this.audioAnalyser.fftSize = 256;

      // Create script processor for real-time audio processing
      const bufferSize = 4096;
      this.processor = this.audioContext.createScriptProcessor(
        bufferSize,
        1,
        1,
      );

      // Connect audio graph
      this.microphone.connect(this.audioAnalyser);
      this.microphone.connect(this.processor);
      // Connect to destination to ensure the processor runs
      // We'll set the gain to 0 to avoid echo
      const gainNode = this.audioContext.createGain();
      gainNode.gain.value = 0; // Mute output to avoid echo
      this.processor.connect(gainNode);
      gainNode.connect(this.audioContext.destination);

      // Calculate downsampling ratio (browser sample rate to 16kHz)
      const inputSampleRate = this.audioContext.sampleRate;
      const targetSampleRate = 16000;
      const downSampleRatio = inputSampleRate / targetSampleRate;

      // Process audio for AssemblyAI
      let audioProcessCount = 0;
      this.processor.onaudioprocess = (event) => {
        // Log every 100th call to avoid console spam
        if (audioProcessCount % 100 === 0) {
          console.log("[AudioRecorder] Audio process callback fired", {
            isRecording: this.isRecording,
            isPaused: this.isPaused,
            hasListener: !!this.listeners.onAudioData,
            count: audioProcessCount,
          });
        }
        audioProcessCount++;

        if (this.isRecording && !this.isPaused && this.listeners.onAudioData) {
          const inputData = event.inputBuffer.getChannelData(0);

          // Downsample to 16kHz if needed
          let processedData = inputData;
          if (inputSampleRate !== targetSampleRate) {
            processedData = this.downsample(inputData, downSampleRatio);
          }

          // Convert Float32Array to Int16Array for AssemblyAI
          const pcmData = this.convertFloat32ToInt16(processedData);

          // Send PCM data to listener
          this.listeners.onAudioData(pcmData.buffer);
        }
      };

      // Setup MediaRecorder event handlers
      console.log("[AudioRecorder] Setting up MediaRecorder event handlers...");
      this.mediaRecorder.ondataavailable = (event) => {
        console.log(
          "[AudioRecorder] Data available event fired:",
          "size =",
          event.data.size,
          "bytes, type =",
          event.data.type,
          "isRecording =",
          this.isRecording,
          "totalChunks =",
          this.recordedChunks.length,
        );

        if (event.data.size > 0) {
          console.log(
            "[AudioRecorder] Adding chunk:",
            event.data.size,
            "bytes",
          );
          this.recordedChunks.push(event.data);
          if (this.listeners.onChunk) {
            console.log("[AudioRecorder] Calling onChunk listener");
            this.listeners.onChunk(event.data);
          } else {
            console.warn("[AudioRecorder] No onChunk listener set!");
          }
        } else {
          console.warn("[AudioRecorder] Data available but size is 0");
        }
      };

      this.mediaRecorder.onstop = () => {
        try {
          if (this.listeners.onStop) {
            const audioBlob = new Blob(this.recordedChunks, {
              type: "audio/webm;codecs=opus",
            });
            this.listeners.onStop(audioBlob);
          }
        } catch (error) {
          console.error("[AudioRecorder] onStop listener threw:", error);
        } finally {
          // The final dataavailable has now been delivered; tear down the mic
          // tracks / audio context AFTER the last audio is captured, then settle
          // the pending stop() promise so callers can safely finalize. In a
          // finally{} so a throwing onStop listener can never strand stop().
          this._settleStop();
        }
      };

      this.mediaRecorder.onerror = (error) => {
        console.error("MediaRecorder error:", error);
        if (this.listeners.onError) {
          this.listeners.onError(error);
        }
        // A recorder error may mean onstop never fires — settle the pending
        // stop() promise so stopRecording() can't await forever.
        this._settleStop();
      };

      // NOTE: the audio-level monitor loop is started in start() (once
      // isRecording is true), not here — the loop is guarded by isRecording.

      console.log("[AudioRecorder] Initialization complete!");
      return stream;
    } catch (error) {
      console.error("[AudioRecorder] Failed to initialize:", error);
      throw error;
    }
  }

  /**
   * Start recording audio
   */
  start() {
    console.log("[AudioRecorder] Start method called");
    if (!this.mediaRecorder) {
      console.error("[AudioRecorder] MediaRecorder not initialized");
      throw new Error("Recorder not initialized");
    }
    if (this.isRecording) {
      console.error("[AudioRecorder] Already recording");
      throw new Error("Already recording");
    }

    console.log(
      "[AudioRecorder] Starting MediaRecorder with chunk duration:",
      this.chunkDuration,
    );
    this.isRecording = true;
    this.isPaused = false;
    this.recordedChunks = [];

    // Kick off the audio-level monitor loop now that isRecording is true. It is
    // guarded by `this.isRecording`, so it must be started here (not in
    // initialize(), where the flag is still false and the loop would no-op).
    this.monitorAudioLevel();

    // Start recording with time slicing for chunked data
    this.mediaRecorder.start(this.chunkDuration);
    console.log(
      "[AudioRecorder] MediaRecorder started, state:",
      this.mediaRecorder.state,
      "chunk duration:",
      this.chunkDuration,
    );

    // Set up a timeout to check if we get the first chunk
    setTimeout(() => {
      console.log("[AudioRecorder] First chunk timeout check:", {
        state: this.mediaRecorder.state,
        chunksReceived: this.recordedChunks.length,
        isRecording: this.isRecording,
        timeElapsed: "2 seconds",
      });

      if (this.recordedChunks.length === 0 && this.isRecording) {
        console.warn(
          "[AudioRecorder] No chunks received after 2 seconds! This might indicate an issue.",
        );
      }
    }, 2000);
  }

  /**
   * Pause recording
   */
  pause() {
    if (this.mediaRecorder && this.mediaRecorder.state === "recording") {
      this.mediaRecorder.pause();
      this.isPaused = true;
    }
  }

  /**
   * Resume recording
   */
  resume() {
    if (this.mediaRecorder && this.mediaRecorder.state === "paused") {
      this.mediaRecorder.resume();
      this.isPaused = false;
    }
  }

  /**
   * Stop recording and cleanup.
   *
   * Resolves only AFTER MediaRecorder's `onstop` fires (which happens after
   * the final async `dataavailable` is delivered), so callers can await the
   * last chunk before finalizing the upload.
   *
   * @returns {Promise<void>}
   */
  stop() {
    return new Promise((resolve) => {
      if (!this.mediaRecorder || this.mediaRecorder.state === "inactive") {
        this._teardown();
        resolve();
        return;
      }
      this.isRecording = false;
      this.isPaused = false;
      this._stopResolve = resolve;
      // Safety net: if neither onstop nor onerror fires (browser edge cases),
      // settle after a timeout so stopRecording() can never hang forever.
      this._stopTimeout = setTimeout(() => {
        console.warn(
          "[AudioRecorder] stop() timed out waiting for onstop; settling",
        );
        this._settleStop();
      }, 5000);
      this.mediaRecorder.stop();
    });
  }

  /**
   * Settle the pending stop() promise exactly once: clears the timeout, tears
   * down resources, and resolves. Idempotent — safe to call from onstop,
   * onerror, or the timeout fallback (whichever happens first wins).
   * @private
   */
  _settleStop() {
    if (this._stopTimeout) {
      clearTimeout(this._stopTimeout);
      this._stopTimeout = null;
    }
    this._teardown();
    if (this._stopResolve) {
      const resolve = this._stopResolve;
      this._stopResolve = null;
      resolve();
    }
  }

  /**
   * Tear down the media stream tracks, audio processing graph, and audio
   * context. Each step is guarded so teardown never throws, regardless of
   * which resources are still present.
   * @private
   */
  _teardown() {
    try {
      this.mediaRecorder?.stream?.getTracks().forEach((track) => track.stop());
    } catch (error) {
      console.error("[AudioRecorder] Failed to stop media tracks:", error);
    }

    try {
      this.cleanup();
    } catch (error) {
      console.error("[AudioRecorder] Failed to cleanup audio graph:", error);
    }

    try {
      if (this.audioContext) {
        this.audioContext.close();
        this.audioContext = null;
      }
    } catch (error) {
      console.error("[AudioRecorder] Failed to close audio context:", error);
    }
  }

  /**
   * Monitor audio levels for visualization
   */
  monitorAudioLevel() {
    if (!this.audioAnalyser || !this.isRecording) return;

    // Time-domain RMS is a much better loudness proxy than averaging frequency
    // bins (most bins are ~0 for speech, which crushes the dynamic range). This
    // only feeds the visualizer via onAudioLevel — it does not affect recording,
    // upload, or transcription (separate audio-graph paths).
    const dataArray = new Uint8Array(this.audioAnalyser.fftSize);
    this.audioAnalyser.getByteTimeDomainData(dataArray);
    const normalizedLevel = rmsLevelFromTimeDomain(dataArray); // 0-1

    if (this.listeners.onAudioLevel) {
      this.listeners.onAudioLevel(normalizedLevel);
    }

    // Continue monitoring if recording
    if (this.isRecording) {
      requestAnimationFrame(() => this.monitorAudioLevel());
    }
  }

  /**
   * Set event listeners
   * @param {Object} listeners
   */
  setListeners(listeners) {
    this.listeners = { ...this.listeners, ...listeners };
  }

  /**
   * Get recording state
   * @returns {string}
   */
  getState() {
    return this.mediaRecorder ? this.mediaRecorder.state : "inactive";
  }

  /**
   * Get the actual MIME type the browser's MediaRecorder chose for recording.
   * This is authoritative (e.g. Safari records "audio/mp4", Chrome/Firefox
   * record "audio/webm;codecs=opus") and should be used to label chunk uploads
   * so the Content-Type matches the actual bytes.
   * @returns {string}
   */
  getMimeType() {
    return this.mediaRecorder?.mimeType || "audio/webm;codecs=opus";
  }

  /**
   * Check if recording is active
   * @returns {boolean}
   */
  isActive() {
    return this.isRecording && !this.isPaused;
  }

  /**
   * Get recorded duration (approximate based on chunks)
   * @returns {number} Duration in seconds
   */
  getDuration() {
    return (this.recordedChunks.length * this.chunkDuration) / 1000;
  }

  /**
   * Downsample audio data
   * @param {Float32Array} buffer - Input audio buffer
   * @param {number} ratio - Downsampling ratio
   * @returns {Float32Array}
   */
  downsample(buffer, ratio) {
    const newLength = Math.round(buffer.length / ratio);
    const result = new Float32Array(newLength);

    let offsetResult = 0;
    let offsetBuffer = 0;

    while (offsetResult < result.length) {
      const nextOffsetBuffer = Math.round((offsetResult + 1) * ratio);
      let accum = 0;
      let count = 0;

      for (
        let i = offsetBuffer;
        i < nextOffsetBuffer && i < buffer.length;
        i++
      ) {
        accum += buffer[i];
        count++;
      }

      result[offsetResult] = accum / count;
      offsetResult++;
      offsetBuffer = nextOffsetBuffer;
    }

    return result;
  }

  /**
   * Convert Float32Array to Int16Array
   * @param {Float32Array} float32Array
   * @returns {Int16Array}
   */
  convertFloat32ToInt16(float32Array) {
    const int16Array = new Int16Array(float32Array.length);

    for (let i = 0; i < float32Array.length; i++) {
      // Convert float32 (-1 to 1) to int16 (-32768 to 32767)
      const s = Math.max(-1, Math.min(1, float32Array[i]));
      int16Array[i] = s < 0 ? s * 0x8000 : s * 0x7fff;
    }

    return int16Array;
  }

  /**
   * Clean up audio processing
   */
  cleanup() {
    if (this.processor) {
      this.processor.disconnect();
      this.processor = null;
    }
    if (this.microphone) {
      this.microphone.disconnect();
      this.microphone = null;
    }
    if (this.audioAnalyser) {
      this.audioAnalyser.disconnect();
      this.audioAnalyser = null;
    }
  }
}

// Create singleton instance
const audioRecorderService = new AudioRecorderService();
export default audioRecorderService;
